import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { Leverantor } from "@/store/modules/leverantorModule";
import cloneDeep from "lodash/cloneDeep";

export type Material = {
    id: number;
    artikelnummer: string;
    namn: string;
    leverantor: string;
    streckkod1: string;
    streckkod2: string;
};

export type MaterialBackend = Material & {
    leverantor_display: Leverantor;
};

type State = BaseState & {
    material: Map<Material["id"], Material>;
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type Getters = {
    materialById: (data: { id: number }) => Material | null;
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        material: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "material";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Den här ska inte anropas direkt. Hämtning ska göras via actionen (fetchMaterial)
         */
        material: (state: State) => [...state.material.values()],
        /**
         * Anropar en generisk getter för hämtning av subset. computed i en komponent som lyssnar på denna getter
         * är förmodligen vad du vill ha i din komponent.
         * Se helpers/baseSubset
         */
        materialSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: "material",
            }),
        /**
         * Om du bara vill hämta ett objekt vid ett enda tillfälle så ska det göras via actionen och inte här
         * (getMaterialById, som hämtar från BE om objektet inte finns i store).
         * Om du vill lyssna på förändringar på ett enskilt objekt så vill du troligen lyssna på den här i en
         * computed i din komponent
         */
        materialById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.material.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Tillägg av nya objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (createMaterial)
         * Se helpers/subset
         */
        addMaterial: (
            state: State,
            {
                offset,
                subsetName = "default",
                material = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                material: Material[];
            }
        ) =>
            subsetSetter<State, Material>({
                offset,
                subsetName,
                state,
                collectionName: "material",
                items: material,
            }),
        /**
         * Uppdatering av objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (updateMaterial)
         * Se helpers/subset
         */
        updateMaterial: (
            state: State,
            {
                material,
                offset,
                subsetName,
            }: {
                material: Material[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Material>({
                offset,
                state,
                collectionName: "material",
                items: material,
            }),
    },
    /**
     * Actions ska alltid innehålla baseActions + det som är specifikt för modulen. Den returnerar resultatet,
     * men det du troligen vill göra är att lyssna på
     */
    actions: {
        ...baseActions({ moduleName }),
        getMaterialById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
            }: {
                id: number;
            }
        ) => {
            const foundMaterial = getters.materialById({ id });

            if (foundMaterial || !id) {
                return foundMaterial;
            }

            try {
                const { data } = await klinikenApi.get<{
                    material: MaterialBackend;
                }>(`operation/material/${id}`, getConfig({}));

                if (data.material) {
                    const retrievedMaterial = data.material;

                    commit("addMaterial", {
                        offset: 0,
                        material: [retrievedMaterial],
                        subsetName: id,
                    });

                    return retrievedMaterial;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta material från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den kapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         */
        fetchMaterial: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, MaterialBackend>({
                    filterParams,
                    state,
                    limit,
                    page,
                    collectionName: "material",
                    apiUrl: "operation/material/",
                });

                if (!results) {
                    if (typeof total === "number") {
                        commit("setTotal", {
                            total,
                            subsetName,
                        });
                    }

                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                // Eftersom leverantörer skickas tillsammans med materialet så plockar vi ut och lagrar det i
                // leverantörmodulen
                const leverantorer = new Map();
                const allMaterial = results.map(({ leverantor_display, ...singleMaterial }) => {
                    if (leverantor_display) {
                        leverantorer.set(leverantor_display.id, leverantor_display);
                    }

                    return singleMaterial;
                });

                commit(
                    "leverantor/addLeverantorer",
                    {
                        offset,
                        leverantorer: [...leverantorer.values()],
                    },
                    { root: true }
                );

                commit("addMaterial", {
                    offset,
                    subsetName,
                    material: allMaterial,
                });
                commit("setTotal", {
                    total,
                    subsetName,
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createMaterial: async (
            { commit, state }: BaseActionsProps<State>,
            {
                material,
            }: {
                material: Material;
            }
        ) => {
            try {
                const response = await klinikenApi.post("operation/material/", material, getConfig({}));

                commit("addMaterial", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    material: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateMaterial: async (
            { commit, state }: BaseActionsProps<State>,
            {
                material,
            }: {
                material: Material;
            }
        ) => {
            try {
                const response = await klinikenApi.put(`operation/material/${material.id}/`, material, getConfig({}));

                commit("updateMaterial", {
                    subsetName: "newlyUpdated",
                    material: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
